import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { AlertTriangle, CheckCircle2, Eye, EyeOff } from 'lucide-react';
import { supabase } from '../integrations/supabase/client';
import { useAuth } from '../components/auth/AuthContext';
import AuthSplit from '../components/auth/AuthSplit';

const ResetPassword: React.FC = () => {
  const { updatePassword } = useAuth();
  const navigate = useNavigate();
  const [checking, setChecking] = useState(true);
  const [hasSession, setHasSession] = useState(false);
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    let cancelled = false;
    void supabase.auth.getSession().then(({ data }) => {
      if (cancelled) return;
      setHasSession(!!data.session);
      setChecking(false);
    });
    return () => { cancelled = true; };
  }, []);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    if (password.length < 8) {
      setError('Use at least 8 characters.');
      return;
    }
    if (password !== confirm) {
      setError('Passwords do not match.');
      return;
    }
    setBusy(true);
    try {
      await updatePassword(password);
      setDone(true);
      setTimeout(() => navigate('/account'), 1800);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not update your password.');
    } finally {
      setBusy(false);
    }
  };

  if (checking) {
    return (
      <AuthSplit
        eyebrow="Student account / Account recovery"
        title="Choose a new password."
        lede="Set a strong password you do not use on another site. You will stay signed in after it is saved."
        notes={
          <div className="space-y-2.5">
            <p>Use at least 8 characters. A short phrase is easier to remember than a single word.</p>
            <p>Changing the password does not change your enrolled programmes or certificate record.</p>
          </div>
        }
      >
        <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-[0_24px_70px_-48px_rgba(15,40,90,0.45)] sm:p-7">
          <div className="h-4 w-32 animate-pulse rounded bg-slate-100" />
          <div className="mt-6 space-y-4">
            <div className="h-12 animate-pulse rounded-xl bg-slate-100" />
            <div className="h-12 animate-pulse rounded-xl bg-slate-100" />
            <div className="h-12 animate-pulse rounded-xl bg-slate-100" />
          </div>
        </div>
      </AuthSplit>
    );
  }

  return (
    <AuthSplit
      eyebrow="Student account / Account recovery"
      title="Choose a new password."
      lede="Set a strong password you do not use on another site. You will stay signed in after it is saved."
      notes={
        <div className="space-y-2.5">
          <p>Use at least 8 characters. A short phrase is easier to remember than a single word.</p>
          <p>Changing the password does not change your enrolled programmes or certificate record.</p>
        </div>
      }
      footer={
        <Link to="/login" className="text-[13px] font-semibold text-blue-700 hover:text-blue-800">
          Back to sign in
        </Link>
      }
    >
      <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-[0_24px_70px_-48px_rgba(15,40,90,0.45)] sm:p-7">
        <div className="flex items-center justify-between gap-3 border-b border-slate-100 pb-4">
          <h2 className="text-[17px] font-semibold tracking-[-0.015em] text-slate-950">New password</h2>
          <span
            className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-[9px] font-semibold uppercase tracking-[0.1em] ${
              done
                ? 'border-emerald-200 bg-emerald-50 text-emerald-700'
                : !hasSession
                  ? 'border-amber-200 bg-amber-50 text-amber-700'
                  : 'border-slate-200 bg-slate-50 text-slate-500'
            }`}
          >
            {done ? 'Updated' : !hasSession ? 'Link invalid' : 'Step 2 of 2'}
          </span>
        </div>

        {done ? (
          <div className="mt-5 rounded-xl border border-emerald-200 bg-emerald-50 p-4 text-[12.5px] leading-5 text-emerald-800">
            <div className="flex items-start gap-2.5">
              <CheckCircle2 className="mt-0.5 h-5 w-5 flex-none" strokeWidth={1.6} />
              <div>
                <p className="font-semibold">Password updated</p>
                <p className="mt-1">You are signed in. Taking you to your account…</p>
              </div>
            </div>
          </div>
        ) : !hasSession ? (
          <div className="mt-5 rounded-xl border border-amber-200 bg-amber-50 p-4 text-[12.5px] leading-5 text-amber-800">
            <div className="flex items-start gap-2.5">
              <AlertTriangle className="mt-0.5 h-5 w-5 flex-none" strokeWidth={1.6} />
              <div>
                <p className="font-semibold">This reset link is invalid or expired</p>
                <p className="mt-1">Request a fresh link and open it in the same browser.</p>
                <Link to="/forgot-password" className="mt-2 inline-block font-semibold underline underline-offset-2">
                  Request a new link
                </Link>
              </div>
            </div>
          </div>
        ) : (
          <form onSubmit={submit} className="mt-5 space-y-4">
            {error && (
              <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-[12.5px] leading-5 text-red-700" role="alert">
                {error}
              </div>
            )}
            <div>
              <label htmlFor="reset-new-password" className="auth-label">New password</label>
              <div className="relative">
                <input
                  id="reset-new-password"
                  type={showPassword ? 'text' : 'password'}
                  required
                  autoComplete="new-password"
                  placeholder="At least 8 characters"
                  className="auth-field pr-12"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                />
                <button
                  type="button"
                  className="absolute inset-y-0 right-0 flex w-11 items-center justify-center text-slate-400 transition-colors hover:text-slate-700"
                  onClick={() => setShowPassword((current) => !current)}
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                >
                  {showPassword ? <EyeOff className="h-4 w-4" strokeWidth={1.7} /> : <Eye className="h-4 w-4" strokeWidth={1.7} />}
                </button>
              </div>
            </div>
            <div>
              <label htmlFor="reset-confirm-password" className="auth-label">Confirm password</label>
              <input
                id="reset-confirm-password"
                type="password"
                required
                autoComplete="new-password"
                placeholder="Repeat your password"
                className="auth-field"
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
              />
            </div>
            <button type="submit" disabled={busy} className="auth-btn-primary">
              {busy ? 'Updating…' : 'Update password'}
            </button>
          </form>
        )}
      </div>
    </AuthSplit>
  );
};

export default ResetPassword;
